import { useState, useEffect } from 'react';
import { DELAY_UNITS, TYPING_UNITS } from '../../../flowConstants';
import TelegramThumb from '../TelegramThumb';
import { AutoDeleteField, MediaUploadLabel } from '../MediaFields';
import AutoGrowTextarea from '../AutoGrowTextarea';

const COMPOSED_ELEMENT_TYPES = [
    { type: 'text',     label: 'Texto',       icon: 'fas fa-font',           color: '#3b82f6' },
    { type: 'image',    label: 'Imagem',      icon: 'fas fa-image',          color: '#22c55e' },
    { type: 'video',    label: 'Vídeo',       icon: 'fas fa-video',          color: '#ef4444' },
    { type: 'audio',    label: 'Áudio',       icon: 'fas fa-music',          color: '#a855f7' },
    { type: 'delay',    label: 'Atraso',      icon: 'fas fa-clock',          color: '#f59e0b' },
    { type: 'typing',   label: 'Digitando',   icon: 'fas fa-keyboard',       color: '#eab308' },
    { type: 'location', label: 'Localização', icon: 'fas fa-map-marker-alt', color: '#06b6d4' },
];

const DEFAULT_CONFIGS = {
    text:     { text: '' },
    image:    { caption: '', media: {} },
    video:    { caption: '', media: {} },
    audio:    { media: {} },
    delay:    { mode: 'fixed', value: 3, unit: 'seconds', seconds: 3 },
    typing:   { action: 'typing', value: 2, unit: 'seconds', seconds: 2 },
    location: {},
};

export default function ComposedNode({ id, data, botId, selected }) {
    const composedElements = data?.composedElements || [];
    const [showAddMenu, setShowAddMenu] = useState(false);

    useEffect(() => {
        if (!selected) setShowAddMenu(false);
    }, [selected]);

    const setElements = (updated) => data?.onChangeComposed?.(id, { composedElements: updated });

    const updateElement = (index, patch) =>
        setElements(composedElements.map((el, i) =>
            i === index ? { ...el, config: { ...(el.config || {}), ...patch } } : el
        ));

    const updateElementMedia = (index, mediaPatch) =>
        setElements(composedElements.map((el, i) =>
            i === index ? { ...el, config: { ...(el.config || {}), media: { ...(el.config?.media || {}), ...mediaPatch } } } : el
        ));

    const moveElement = (index, dir) => {
        const arr = [...composedElements];
        const swapIdx = index + dir;
        if (swapIdx < 0 || swapIdx >= arr.length) return;
        [arr[index], arr[swapIdx]] = [arr[swapIdx], arr[index]];
        setElements(arr);
    };

    const removeElement = (index) => setElements(composedElements.filter((_, i) => i !== index));

    const addElement = (type) => {
        const elId = `e_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        setElements([...composedElements, { type, elId, config: DEFAULT_CONFIGS[type] || {} }]);
        setShowAddMenu(false);
    };

    return (
        <div className="mt-2 space-y-1.5">
            {composedElements.length === 0 && (
                <div className="text-[10px] text-gray-500 text-center py-2 border border-dashed border-violet-500/30 rounded">
                    Nenhum elemento. Clique em + para adicionar.
                </div>
            )}

            {composedElements.map((el, index) => {
                const elMeta = COMPOSED_ELEMENT_TYPES.find((t) => t.type === el.type);
                const elColor = elMeta?.color || '#8b5cf6';
                const elMedia = el.config?.media || {};
                const elId = el.elId || `${id}_${index}`;

                return (
                    <div key={elId} className="border border-white/[0.06] rounded px-2 py-1.5 space-y-1.5"
                        style={{ background: `${elColor}08` }}>

                        {/* Element header */}
                        <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                                style={{ backgroundColor: `${elColor}22` }}>
                                <i className={`${elMeta?.icon || 'fas fa-circle'} text-[8px]`} style={{ color: elColor }} />
                            </div>
                            <span className="text-[9px] font-semibold flex-1" style={{ color: elColor }}>
                                {elMeta?.label || el.type}
                            </span>
                            <div className="flex gap-1 shrink-0">
                                <button type="button" className="text-[10px] text-gray-600 hover:text-gray-300 disabled:opacity-20"
                                    onClick={() => moveElement(index, -1)} disabled={index === 0}>
                                    <i className="fas fa-chevron-up" />
                                </button>
                                <button type="button" className="text-[10px] text-gray-600 hover:text-gray-300 disabled:opacity-20"
                                    onClick={() => moveElement(index, 1)} disabled={index === composedElements.length - 1}>
                                    <i className="fas fa-chevron-down" />
                                </button>
                                <button type="button" className="text-[10px] text-red-500 hover:text-red-400"
                                    onClick={() => removeElement(index)}>
                                    <i className="fas fa-times" />
                                </button>
                            </div>
                        </div>

                        {el.type === 'text' && (
                            <AutoGrowTextarea className="spt-textarea border-2 text-[10px]" placeholder="Texto da mensagem"
                                value={(el.config || {}).text || ''}
                                onChange={(e) => updateElement(index, { text: e.target.value })} />
                        )}

                        {el.type === 'image' && (
                            <div className="space-y-1.5">
                                {data?.uploadingElementId === elId ? (
                                    <div className="border-2 border-dashed rounded-lg p-3 border-white/[0.06] flex flex-col items-center gap-1">
                                        <i className="fas fa-spinner fa-spin text-white/40 text-lg" />
                                        <p className="text-[10px] text-white/50">Enviando...</p>
                                        <p className="text-[9px] text-white/30">aguarde</p>
                                    </div>
                                ) : elMedia.photo ? (
                                    <>
                                        <label className="relative border-2 border-dashed rounded-lg p-3 transition-colors cursor-pointer border-white/[0.06] hover:border-white/[0.08] block">
                                            <input accept="image/*" className="hidden" type="file"
                                                onChange={(e) => { const f = e.target.files?.[0]; if (f) data?.onUploadComposedMedia?.(id, elId, 'photo', f, index); e.target.value = ''; }} />
                                            {/* <button
                                                type="button"
                                                className="absolute top-1 right-1 text-red-400 hover:text-red-300 z-10 text-[10px]"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateElement(index, { media: {} }); }}
                                            >
                                                <i className="fas fa-trash" />
                                            </button> */}
                                            <TelegramThumb fileId={elMedia.photo} botId={botId}
                                                imgClassName="w-full max-h-24 object-contain rounded-lg" />
                                            <p className="text-[10px] text-white/50 truncate max-w-full mt-1">
                                                {elMedia.fileName || elMedia.photo}
                                            </p>
                                            <p className="text-[9px] text-white/30">Clique para trocar</p>
                                        </label>
                                        <input type="text" className="input3 w-full text-[10px]" placeholder="Legenda (opcional)"
                                            value={(el.config || {}).caption || ''}
                                            onChange={(e) => updateElement(index, { caption: e.target.value })} />
                                        <label className="flex items-center gap-1.5 text-[9px] text-gray-400 cursor-pointer">
                                            <input type="checkbox" className="h-2.5 w-2.5 rounded border-white/20 bg-white/10"
                                                checked={Boolean(elMedia.has_spoiler)}
                                                onChange={(e) => updateElementMedia(index, { has_spoiler: e.target.checked })} />
                                            Spoiler
                                        </label>
                                        <AutoDeleteField compact value={elMedia.auto_delete_seconds}
                                            onToggle={(c) => updateElementMedia(index, { auto_delete_seconds: c ? 5 : null })}
                                            onChangeSeconds={(v) => updateElementMedia(index, { auto_delete_seconds: v })} />
                                    </>
                                ) : (
                                    <MediaUploadLabel accept="image/*"
                                        onUpload={(f) => data?.onUploadComposedMedia?.(id, elId, 'photo', f, index)} />
                                )}
                            </div>
                        )}

                        {el.type === 'video' && (
                            <div className="space-y-1.5">
                                {data?.uploadingElementId === elId ? (
                                    <div className="border-2 border-dashed rounded-lg p-3 border-white/[0.06] flex flex-col items-center gap-1">
                                        <i className="fas fa-spinner fa-spin text-white/40 text-lg" />
                                        <p className="text-[10px] text-white/50">Enviando...</p>
                                        <p className="text-[9px] text-white/30">aguarde</p>
                                    </div>
                                ) : elMedia.video ? (
                                    <>
                                        <label className="relative border-2 border-dashed rounded-lg p-3 transition-colors cursor-pointer border-white/[0.06] hover:border-white/[0.08] block">
                                            <input accept="video/*" className="hidden" type="file"
                                                onChange={(e) => { const f = e.target.files?.[0]; if (f) data?.onUploadComposedMedia?.(id, elId, 'video', f, index); e.target.value = ''; }} />
                                            {/* <button
                                                type="button"
                                                className="absolute top-1 right-1 text-red-400 hover:text-red-300 z-10 text-[10px]"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateElement(index, { media: {} }); }}
                                            >
                                                <i className="fas fa-trash" />
                                            </button> */}
                                            <div className="flex items-center justify-center min-h-[4rem]">
                                                {elMedia.thumbnail
                                                    ? <TelegramThumb fileId={elMedia.thumbnail} botId={botId}
                                                        imgClassName="w-full max-h-24 object-contain rounded-lg" />
                                                    : <i className="fas fa-play-circle text-red-400 text-xl opacity-80" />
                                                }
                                            </div>
                                            <p className="text-[10px] text-white/50 truncate max-w-full mt-1">
                                                {elMedia.fileName || elMedia.video}
                                            </p>
                                            <p className="text-[9px] text-white/30">Clique para trocar</p>
                                        </label>
                                        <input type="text" className="input3 w-full text-[10px]" placeholder="Legenda (opcional)"
                                            value={(el.config || {}).caption || ''}
                                            onChange={(e) => updateElement(index, { caption: e.target.value })} />
                                        <label className="flex items-center gap-1.5 text-[9px] text-gray-400 cursor-pointer">
                                            <input type="checkbox" className="h-2.5 w-2.5 rounded border-white/20 bg-white/10"
                                                checked={Boolean(elMedia.has_spoiler)}
                                                onChange={(e) => updateElementMedia(index, { has_spoiler: e.target.checked })} />
                                            Spoiler
                                        </label>
                                        <AutoDeleteField compact value={elMedia.auto_delete_seconds}
                                            onToggle={(c) => updateElementMedia(index, { auto_delete_seconds: c ? 5 : null })}
                                            onChangeSeconds={(v) => updateElementMedia(index, { auto_delete_seconds: v })} />
                                    </>
                                ) : (
                                    <MediaUploadLabel accept="video/*"
                                        onUpload={(f) => data?.onUploadComposedMedia?.(id, elId, 'video', f, index)} />
                                )}
                            </div>
                        )}

                        {el.type === 'audio' && (
                            <div className="space-y-1.5">
                                {data?.uploadingElementId === elId ? (
                                    <div className="border-2 border-dashed rounded-lg p-3 border-white/[0.06] flex flex-col items-center gap-1">
                                        <i className="fas fa-spinner fa-spin text-white/40 text-lg" />
                                        <p className="text-[10px] text-white/50">Enviando...</p>
                                        <p className="text-[9px] text-white/30">aguarde</p>
                                    </div>
                                ) : (elMedia.audio || elMedia.voice) ? (
                                    <>
                                        <label className="relative border-2 border-dashed rounded-lg p-3 transition-colors cursor-pointer border-white/[0.06] hover:border-white/[0.08] block">
                                            <input accept="audio/*" className="hidden" type="file"
                                                onChange={(e) => { const f = e.target.files?.[0]; if (f) data?.onUploadComposedMedia?.(id, elId, 'audio', f, index); e.target.value = ''; }} />
                                            {/* <button
                                                type="button"
                                                className="absolute top-1 right-1 text-red-400 hover:text-red-300 z-10 text-[10px]"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateElement(index, { media: {} }); }}
                                            >
                                                <i className="fas fa-trash" />
                                            </button> */}
                                            <div className="flex items-center justify-center py-2">
                                                <i className="fas fa-music text-purple-400 text-2xl opacity-80" />
                                            </div>
                                            <p className="text-[10px] text-white/50 truncate max-w-full mt-1">
                                                {elMedia.fileName || elMedia.audio || elMedia.voice}
                                            </p>
                                            <p className="text-[9px] text-white/30">Clique para trocar</p>
                                        </label>
                                        <AutoDeleteField compact value={elMedia.auto_delete_seconds}
                                            onToggle={(c) => updateElementMedia(index, { auto_delete_seconds: c ? 5 : null })}
                                            onChangeSeconds={(v) => updateElementMedia(index, { auto_delete_seconds: v })} />
                                    </>
                                ) : (
                                    <MediaUploadLabel accept="audio/*"
                                        onUpload={(f) => data?.onUploadComposedMedia?.(id, elId, 'audio', f, index)} />
                                )}
                            </div>
                        )}

                        {el.type === 'delay' && (
                            <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                    <input type="number" min="1" step="1" className="input3 w-1/2 text-[10px]"
                                        value={el.config?.value ?? el.config?.seconds ?? 3}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 1;
                                            const unit = el.config?.unit || 'seconds';
                                            const factor = DELAY_UNITS.find((u) => u.value === unit)?.factor || 1;
                                            updateElement(index, { value: val, unit, seconds: Math.round(val * factor), mode: 'fixed' });
                                        }} />
                                    <select className="form-select text-[10px] flex-1" value={el.config?.unit || 'seconds'}
                                        onChange={(e) => {
                                            const unit = e.target.value;
                                            const factor = DELAY_UNITS.find((u) => u.value === unit)?.factor || 1;
                                            const val = el.config?.value ?? el.config?.seconds ?? 3;
                                            updateElement(index, { value: val, unit, seconds: Math.round(val * factor), mode: 'fixed' });
                                        }}>
                                        {DELAY_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                                    </select>
                                </div>
                                <label className="flex items-center gap-1.5 text-[9px] text-gray-400 cursor-pointer">
                                    <input type="checkbox" className="h-2.5 w-2.5 rounded border-white/20 bg-white/10"
                                        checked={Boolean(el.config?.show_typing)}
                                        onChange={(e) => updateElement(index, { show_typing: e.target.checked })} />
                                    Mostrar "digitando..."
                                </label>
                            </div>
                        )}

                        {el.type === 'typing' && (
                            <div className="space-y-1">
                                <select className="form-select text-[10px] w-full" value={(el.config || {}).action || 'typing'}
                                    onChange={(e) => updateElement(index, { action: e.target.value })}>
                                    <option value="typing">Digitando...</option>
                                    <option value="record_voice">Gravando áudio...</option>
                                </select>
                                <div className="flex items-center gap-1.5">
                                    <input type="number" min="1" step="1" className="input3 w-1/2 text-[10px]"
                                        value={el.config?.value ?? el.config?.seconds ?? 2}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 1;
                                            const unit = el.config?.unit || 'seconds';
                                            const factor = TYPING_UNITS.find((u) => u.value === unit)?.factor || 1;
                                            updateElement(index, { value: val, unit, seconds: Math.round(val * factor) });
                                        }} />
                                    <select className="form-select text-[10px] flex-1" value={el.config?.unit || 'seconds'}
                                        onChange={(e) => {
                                            const unit = e.target.value;
                                            const factor = TYPING_UNITS.find((u) => u.value === unit)?.factor || 1;
                                            const val = el.config?.value ?? el.config?.seconds ?? 2;
                                            updateElement(index, { value: val, unit, seconds: Math.round(val * factor) });
                                        }}>
                                        {TYPING_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}

                        {el.type === 'location' && (
                            <div className="text-[9px] text-cyan-400 flex items-center gap-1">
                                <i className="fas fa-map-marker-alt" />
                                Localização aproximada do lead
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="relative">
                <button type="button"
                    className="w-full text-[10px] text-violet-500 hover:text-violet-300 border border-violet-500/30 rounded py-1"
                    onClick={() => setShowAddMenu((v) => !v)}>
                    <i className="fas fa-plus mr-1" />Adicionar elemento
                </button>
                {showAddMenu && (
                    <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#050505] border border-white/[0.06] rounded-lg p-1.5 shadow-xl z-50 grid grid-cols-3 gap-1">
                        {COMPOSED_ELEMENT_TYPES.map((t) => (
                            <button key={t.type} type="button"
                                className="flex flex-col items-center gap-1 p-2 rounded-md hover:bg-white/[0.06] transition-colors"
                                onClick={() => addElement(t.type)}>
                                <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${t.color}20` }}>
                                    <i className={`${t.icon} text-[10px]`} style={{ color: t.color }} />
                                </div>
                                <span className="text-[9px] text-white/50">{t.label}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
